import React from "react";
import { motion } from "framer-motion";
import {
  FaDraftingCompass,
  FaTools,
  FaCogs,
  FaChartLine,
  FaSyncAlt,
  FaExchangeAlt,
  FaTrashAlt,
  FaProjectDiagram,
  FaServer,
  FaLayerGroup,
} from "react-icons/fa";

const steps = [
  { label: "Design", icon: <FaDraftingCompass /> },
  { label: "Build", icon: <FaTools /> },
  { label: "Operations", icon: <FaCogs /> },
  { label: "Optimization", icon: <FaChartLine /> },
  { label: "Upgradation", icon: <FaSyncAlt /> },
  { label: "Migration", icon: <FaExchangeAlt /> },
  { label: "Deletion", icon: <FaTrashAlt /> },
];

const CloudWorkflow = () => {
  return (
    <section
      id="home"
      style={{
        background: "#020617",
        padding: "50px 20px 60px",
        color: "#e5e7eb",
        borderTop: "1px solid rgba(148,163,184,0.12)",
        fontFamily: "'Roboto', sans-serif",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Company Name */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: "center",
            fontSize: "2.8rem",
            fontWeight: 800,
            marginBottom: "15px",
            marginTop: "5px",
            color: "#38bdf8",
          }}
        >
          Radiance Cloud Solutions
        </motion.h2>

        {/* Short Description */}
        <p
          style={{
            textAlign: "center",
            maxWidth: "800px",
            margin: "0 auto 40px",
            color: "#94a3b8",
            fontSize: "1rem",
            lineHeight: 1.5,
          }}
        >
          End-to-end cloud management from architecture design to secure resource
          decommissioning.
        </p>

        {/* Workflow Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "30px",
            marginBottom: "30px",
          }}
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.12 }}
              whileHover={{ scale: 1.08 }}
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(148,163,184,0.15)",
                borderRadius: "18px",
                padding: "28px 18px",
                textAlign: "center",
                backdropFilter: "blur(6px)",
              }}
            >
              <motion.div
                animate={iconAnimation(step.label)}
                transition={iconTransition(step.label)}
                style={{
                  fontSize: "28px",
                  color: "#38bdf8",
                  marginBottom: "14px",
                }}
              >
                {step.icon}
              </motion.div>

              <div style={{ fontSize: "0.95rem", fontWeight: 600 }}>
                {step.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Service Type Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{
            background:
              "linear-gradient(135deg, rgba(56,189,248,0.15), rgba(99,102,241,0.1))",
            border: "1px solid rgba(56,189,248,0.4)",
            borderRadius: "20px",
            padding: "22px 28px",
            textAlign: "center",
            marginBottom: "35px",
            color: "#38bdf8",
            fontSize: "1.05rem",
            fontWeight: 600,
            backdropFilter: "blur(6px)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
          }}
          whileHover={{ scale: 1.03 }}
        >
          <FaServer style={{ marginRight: "8px", verticalAlign: "middle" }} />
          From <strong>traditional Virtual Machines</strong> to{" "}
          <strong>cutting-edge Kubernetes (K8s) clusters</strong>, we manage it
          all.
        </motion.div>

{/* Mode of Deployments & Environments */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.7 }}
  style={{
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(148,163,184,0.15)",
    borderRadius: "16px",
    padding: "22px 16px",            // reduced outer spacing
    textAlign: "center",
    marginBottom: "28px",
    backdropFilter: "blur(6px)",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",            // vertical alignment fix
    gap: "28px",                     // tighter gap
  }}
>
  {/* Mode of Deployments */}
  <div
    style={{
      minWidth: "240px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <h4
      style={{
        fontSize: "1.1rem",
        fontWeight: 700,
        color: "#38bdf8",
        marginBottom: "8px",
      }}
    >
      <FaProjectDiagram style={{ marginRight: "8px" }} />
      Mode of Deployments
    </h4>
    <p
      style={{
        fontSize: "0.95rem",
        color: "#c7d2fe",
        margin: 0,
      }}
    >
      Manual Deployment | DevOps Pipelines
    </p>
  </div>

{/* ⭐ Infrastructure as Code – Premium Highlight */}
<motion.div
  animate={{
    y: [0, -6, 0],
  }}
  transition={{
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut",
  }}
  whileHover={{
    scale: 1.08,
  }}
  style={{
    minWidth: "240px",
    padding: "16px 18px",
    borderRadius: "16px",
    background:
      "linear-gradient(135deg, rgba(15,23,42,0.9), rgba(30,41,59,0.9))",
    border: "1px solid rgba(56,189,248,0.6)",
    position: "relative",
    overflow: "hidden",
    boxShadow:
      "0 10px 35px rgba(0,0,0,0.45), 0 0 25px rgba(56,189,248,0.35)",
  }}
>
  {/* Rotating automation ring */}
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
    style={{
      position: "absolute",
      inset: "-40%",
      borderRadius: "50%",
      border: "2px dashed rgba(56,189,248,0.35)",
    }}
  />

  {/* Pipeline scan shimmer */}
  <motion.div
    animate={{ x: ["-100%", "120%"] }}
    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      width: "45%",
      background:
        "linear-gradient(120deg, transparent, rgba(56,189,248,0.18), transparent)",
    }}
  />

  {/* Content */}
  <div style={{ position: "relative", zIndex: 1 }}>
    <h4
      style={{
        fontSize: "1.15rem",
        fontWeight: 800,
        color: "#38bdf8",
        marginBottom: "6px",
      }}
    >
      Infrastructure as Code
    </h4>

    <p
      style={{
        fontSize: "0.95rem",
        color: "#e0f2fe",
        margin: 0,
        fontWeight: 600,
      }}
    >
      Single-Click Delivery
    </p>
  </div>

  {/* Bottom glow pulse */}
  <motion.div
    animate={{ opacity: [0.2, 0.6, 0.2] }}
    transition={{ duration: 2.8, repeat: Infinity }}
    style={{
      position: "absolute",
      left: "20%",
      right: "20%",
      bottom: "-2px",
      height: "2px",
      background:
        "linear-gradient(90deg, transparent, #38bdf8, transparent)",
    }}
  />
</motion.div>

  {/* Environments */}
  <div
    style={{
      minWidth: "240px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <h4
      style={{
        fontSize: "1.1rem",
        fontWeight: 700,
        color: "#38bdf8",
        marginBottom: "8px",
      }}
    >
      <FaLayerGroup style={{ marginRight: "8px" }} />
      Environments
    </h4>
    <p
      style={{
        fontSize: "0.95rem",
        color: "#c7d2fe",
        margin: 0,
      }}
    >
      Dev | CDT | Stage | Pre-prod | Prod
    </p>
  </div>
</motion.div>



        {/* Get Free Consultation / Get Quote Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", marginBottom: "5px" }}
        >
          <motion.a
            href="#leadform"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              boxShadow: [
                "0 0 0px rgba(56,189,248,0.4)",
                "0 0 20px rgba(56,189,248,0.8)",
                "0 0 0px rgba(56,189,248,0.4)",
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              display: "inline-block",
              padding: "14px 36px",
              borderRadius: "999px",
              background:
                "linear-gradient(135deg, #38bdf8, #6366f1)",
              color: "#020617",
              fontSize: "1rem",
              fontWeight: 700,
              textDecoration: "none",
              gap: "20px"
            }}
          >
            Get Free Consultation / Quote
          </motion.a>
        </motion.div>

        {/* Cloud Partners */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px", // space between heading and logos
            flexWrap: "wrap",
            marginTop: "30px",
            marginBottom: "20px",
            fontSize: "1.4rem",
            color: "#e5e7eb",
            fontWeight: 600,
          }}
        >
          <span>Our Cloud Partners:</span>
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/a/a8/Microsoft_Azure_Logo.svg"
            alt="Microsoft Azure"
            style={logoStyle}
          />
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg"
            alt="AWS"
            style={logoStyle}
          />
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/5/51/Google_Cloud_logo.svg"
            alt="GCP"
            style={logoStyle}
          />
        </motion.div>
      </div>
    </section>
  );
};

/* ---------- Animation Logic ---------- */
const iconAnimation = (label) => {
  switch (label) {
    case "Design":
      return { rotate: [0, 360] };
    case "Build":
      return { rotate: [-15, 15, -15] };
    case "Operations":
      return { rotate: [0, 360], scale: [1, 1.1, 1] };
    case "Optimization":
      return { scale: [1, 1.2, 1] };
    case "Upgradation":
      return { y: [0, -10, 0] };
    case "Migration":
      return { x: [0, 10, -10, 0] };
    case "Deletion":
      return { opacity: [1, 0.3, 1], scale: [1, 0.6, 1] };
    default:
      return {};
  }
};

const iconTransition = (label) => {
  switch (label) {
    case "Design":
      return { duration: 2, repeat: Infinity, ease: "linear" };
    case "Build":
      return { duration: 0.8, repeat: Infinity, ease: "easeInOut" };
    case "Operations":
      return { duration: 2.5, repeat: Infinity, ease: "linear" };
    case "Optimization":
      return { duration: 1.5, repeat: Infinity, ease: "easeInOut" };
    case "Upgradation":
      return { duration: 1.2, repeat: Infinity, ease: "easeInOut" };
    case "Migration":
      return { duration: 1.5, repeat: Infinity, ease: "easeInOut" };
    case "Deletion":
      return { duration: 1.2, repeat: Infinity, ease: "easeInOut" };
    default:
      return {};
  }
};

const logoStyle = {
  height: "28px",
  padding: "10px",
  opacity: 1,
};

export default CloudWorkflow;
